import { loadTemplate } from "./utils";

export interface AccessCardTemplateData {
  brideName: string;
  groomName: string;
  eventName?: string;
  showEventName: boolean;
  date: string;
  time: string;
  venue: string;
  guestName?: string;
  showGuestName: boolean;
  showAdmitsOneOnly: boolean;
  textColor: string;
  backgroundColor: string;
  backgroundImage?: string;
  overlayColor?: string;
  overlayOpacity?: number;
  backgroundType: "color" | "image";
  qrCode?: string; // PNG base64 data URI (e.g., "data:image/png;base64,...")
  qrPosition?: "top-left" | "top-center" | "top-right" | "middle-left" | "center" | "middle-right" | "bottom-left" | "bottom-center" | "bottom-right" | "custom";
  qrSize?: number;
}

export async function generateDefaultAccessCardHTML(data: AccessCardTemplateData): Promise<string> {
  // Load template from public folder
  const template = await loadTemplate("/templates/access-card-default.html?v=20260823-responsive-couple-names");
  return populateTemplate(template, data);
}

export function populateTemplate(template: string, data: AccessCardTemplateData): string {
  const {
    brideName = "BRIDE",
    groomName = "GROOM",
    eventName = "Reception",
    showEventName = true,
    date = "",
    time = "",
    venue = "",
    guestName = "YOUR GUEST",
    showGuestName,
    showAdmitsOneOnly,
    textColor,
    backgroundColor,
    backgroundImage,
    overlayColor = "#000000",
    overlayOpacity = 0.7,
    backgroundType,
    qrCode,
    qrPosition = "center",
    qrSize = 100,
  } = data;
  const effectiveQrPosition = qrPosition === "middle-left" || qrPosition === "middle-right"
    ? "center"
    : qrPosition;

  // SVG Icons as data URIs
  const calendarIcon = `data:image/svg+xml,%3Csvg width='23' height='23' viewBox='0 0 23 23' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M18.6875 3.23438H16.1719V2.15625C16.1719 2.06094 16.134 1.96953 16.0666 1.90213C15.9992 1.83474 15.9078 1.79688 15.8125 1.79688C15.7172 1.79688 15.6258 1.83474 15.5584 1.90213C15.491 1.96953 15.4531 2.06094 15.4531 2.15625V3.23438H7.54688V2.15625C7.54688 2.06094 7.50901 1.96953 7.44162 1.90213C7.37422 1.83474 7.28281 1.79688 7.1875 1.79688C7.09219 1.79688 7.00078 1.83474 6.93338 1.90213C6.86599 1.96953 6.82812 2.06094 6.82812 2.15625V3.23438H4.3125C4.02656 3.23438 3.75234 3.34796 3.55015 3.55015C3.34796 3.75234 3.23438 4.02656 3.23438 4.3125V18.6875C3.23438 18.9734 3.34796 19.2477 3.55015 19.4498C3.75234 19.652 4.02656 19.7656 4.3125 19.7656H18.6875C18.9734 19.7656 19.2477 19.652 19.4498 19.4498C19.652 19.2477 19.7656 18.9734 19.7656 18.6875V4.3125C19.7656 4.02656 19.652 3.75234 19.4498 3.55015C19.2477 3.34796 18.9734 3.23438 18.6875 3.23438ZM4.3125 3.95312H6.82812V5.03125C6.82812 5.12656 6.86599 5.21797 6.93338 5.28537C7.00078 5.35276 7.09219 5.39062 7.1875 5.39062C7.28281 5.39062 7.37422 5.35276 7.44162 5.28537C7.50901 5.21797 7.54688 5.12656 7.54688 5.03125V3.95312H15.4531V5.03125C15.4531 5.12656 15.491 5.21797 15.5584 5.28537C15.6258 5.35276 15.7172 5.39062 15.8125 5.39062C15.9078 5.39062 15.9992 5.35276 16.0666 5.28537C16.134 5.21797 16.1719 5.12656 16.1719 5.03125V3.95312H18.6875C18.7828 3.95312 18.8742 3.99099 18.9416 4.05838C19.009 4.12578 19.0469 4.21719 19.0469 4.3125V7.54688H3.95312V4.3125C3.95312 4.21719 3.99099 4.12578 4.05838 4.05838C4.12578 3.99099 4.21719 3.95312 4.3125 3.95312ZM18.6875 19.0469H4.3125C4.21719 19.0469 4.12578 19.009 4.05838 18.9416C3.99099 18.8742 3.95312 18.7828 3.95312 18.6875V8.26562H19.0469V18.6875C19.0469 18.7828 19.009 18.8742 18.9416 18.9416C18.8742 19.009 18.7828 19.0469 18.6875 19.0469Z' fill='${encodeURIComponent(textColor)}'/%3E%3C/svg%3E`;
  const mapPinIcon = `data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15.5 12.4721V22.0009C15.5 22.1335 15.5527 22.2607 15.6464 22.3544C15.7402 22.4482 15.8674 22.5009 16 22.5009C16.1326 22.5009 16.2598 22.4482 16.3536 22.3544C16.4473 22.2607 16.5 22.1335 16.5 22.0009V12.4721C17.643 12.3443 18.6937 11.7838 19.4363 10.9055C20.1789 10.0272 20.557 8.89796 20.493 7.74961C20.429 6.60127 19.9278 5.52101 19.0922 4.73069C18.2566 3.94038 17.1501 3.5 16 3.5C14.8499 3.5 13.7434 3.94038 12.9078 4.73069C12.0722 5.52101 11.571 6.60127 11.507 7.74961C11.443 8.89796 11.8211 10.0272 12.5637 10.9055C13.3063 11.7838 14.357 12.3443 15.5 12.4721ZM16 4.50089C16.6922 4.50089 17.3689 4.70616 17.9445 5.09074C18.5201 5.47533 18.9687 6.02195 19.2336 6.66149C19.4985 7.30104 19.5678 8.00477 19.4327 8.6837C19.2977 9.36264 18.9644 9.98628 18.4749 10.4758C17.9854 10.9652 17.3617 11.2986 16.6828 11.4336C16.0039 11.5687 15.3001 11.4994 14.6606 11.2345C14.0211 10.9696 13.4744 10.521 13.0899 9.94538C12.7053 9.36981 12.5 8.69312 12.5 8.00089C12.5 7.07263 12.8687 6.18239 13.5251 5.52601C14.1815 4.86964 15.0717 4.50089 16 4.50089ZM29.5 22.0009C29.5 23.5584 28.0338 24.9796 25.375 26.0009C22.8638 26.9671 19.535 27.5009 16 27.5009C12.465 27.5009 9.13875 26.9696 6.625 26.0009C3.96375 24.9771 2.5 23.5559 2.5 22.0009C2.5 19.6821 5.72625 17.7234 10.9213 16.8909C10.9881 16.8753 11.0574 16.8736 11.1249 16.886C11.1924 16.8984 11.2567 16.9245 11.3136 16.9628C11.3706 17.0011 11.4191 17.0507 11.4561 17.1085C11.493 17.1663 11.5177 17.2311 11.5285 17.2989C11.5394 17.3666 11.5361 17.4359 11.519 17.5024C11.5019 17.5689 11.4713 17.6311 11.4291 17.6852C11.387 17.7393 11.3341 17.7842 11.2738 17.817C11.2135 17.8498 11.1471 17.8699 11.0787 17.8759C6.61625 18.5934 3.5 20.2884 3.5 22.0009C3.5 24.1746 8.5225 26.5009 16 26.5009C23.4775 26.5009 28.5 24.1746 28.5 22.0009C28.5 20.2884 25.3837 18.5934 20.9212 17.8759C20.8539 17.8686 20.7887 17.8476 20.7297 17.8143C20.6706 17.781 20.619 17.7361 20.5779 17.6822C20.5368 17.6284 20.507 17.5667 20.4904 17.501C20.4739 17.4353 20.4709 17.3669 20.4813 17.2996C20.4917 17.2323 20.5154 17.1675 20.5509 17.1097C20.5864 17.0518 20.6331 17.0021 20.6881 16.9634C20.7431 16.9248 20.8053 16.8981 20.871 16.8849C20.9368 16.8718 21.0047 16.8725 21.07 16.8871C26.2737 17.7234 29.5 19.6821 29.5 22.0009Z' fill='${encodeURIComponent(textColor)}'/%3E%3C/svg%3E`;

  // Use placeholder QR code if not provided
  const qrCodeDataURI = qrCode || "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'%3E%3Crect fill='%23000' width='100' height='100'/%3E%3C/svg%3E";

  // Conditional styles
  const backgroundImageStyle = backgroundType === "image" && backgroundImage 
    ? `background-image: url(${backgroundImage});` 
    : "";
  const overlayDisplay = backgroundType === "image" && backgroundImage ? "" : "display: none;";
  const eventNameDisplay = showEventName ? "" : "display: none;";
  const guestNameDisplay = showGuestName ? "" : "display: none;";
  const admitsBadgeDisplay = showAdmitsOneOnly ? "" : "display: none;";
  const qrPositionStyle = {
    "top-left": "position: absolute; top: 24px; left: 24px;",
    "top-center": "position: absolute; top: 24px; left: 50%; transform: translateX(-50%);",
    "top-right": "position: absolute; top: 24px; right: 24px;",
    "middle-left": "position: absolute; top: 50%; left: 24px; transform: translateY(-50%);",
    "middle-right": "position: absolute; top: 50%; right: 24px; transform: translateY(-50%);",
    "bottom-left": "position: absolute; bottom: 24px; left: 24px;",
    "bottom-center": "position: absolute; bottom: 24px; left: 50%; transform: translateX(-50%);",
    "bottom-right": "position: absolute; bottom: 24px; right: 24px;",
    center: "",
    custom: "",
  }[effectiveQrPosition];
  const qrContentStyle = {
    "top-left": `padding-top: ${qrSize + 72}px;`,
    "top-center": `padding-top: ${qrSize + 72}px;`,
    "top-right": `padding-top: ${qrSize + 72}px;`,
    "middle-left": `padding-left: ${qrSize + 48}px;`,
    "middle-right": `padding-right: ${qrSize + 48}px;`,
    "bottom-left": `padding-bottom: ${qrSize + 72}px;`,
    "bottom-center": `padding-bottom: ${qrSize + 72}px;`,
    "bottom-right": `padding-bottom: ${qrSize + 72}px;`,
    center: "",
    custom: "",
  }[effectiveQrPosition];
  const contentScale = Math.max(0.72, 1 - Math.max(0, qrSize - 100) * 0.0028);

  // Font paths - use absolute URLs to avoid CORS issues in iframe
  const origin = typeof window !== 'undefined' ? window.location.origin : '/';
  const optimaFont = `${origin}/fonts/Optima.ttf`;
  const snellFont = `${origin}/fonts/SnellBT-Regular.otf`;
  const glacialFont = `${origin}/fonts/GlacialIndifference-Regular.otf`;

  // Replace all template variables
  return template
    .replace(/\{\{BRIDE_NAME\}\}/g, brideName.toUpperCase())
    .replace(/\{\{GROOM_NAME\}\}/g, groomName.toUpperCase())
    .replace(/\{\{EVENT_NAME\}\}/g, eventName)
    .replace(/\{\{DATE\}\}/g, date)
    .replace(/\{\{TIME\}\}/g, time)
    .replace(/\{\{VENUE\}\}/g, venue || "Balmoral Hall, Victoria Island")
    .replace(/\{\{GUEST_NAME\}\}/g, guestName.toUpperCase())
    .replace(/\{\{TEXT_COLOR\}\}/g, textColor)
    .replace(/\{\{BACKGROUND_COLOR\}\}/g, backgroundColor)
    .replace(/\{\{OVERLAY_COLOR\}\}/g, overlayColor)
    .replace(/\{\{OVERLAY_OPACITY\}\}/g, String(overlayOpacity))
    .replace(/\{\{CALENDAR_ICON\}\}/g, calendarIcon)
    .replace(/\{\{MAP_PIN_ICON\}\}/g, mapPinIcon)
    .replace(/\{\{QR_CODE\}\}/g, qrCodeDataURI)
    .replace(/\{\{QR_SIZE\}\}/g, String(qrSize))
    .replace(/\{\{CONTENT_SCALE\}\}/g, String(contentScale))
    .replace(/\{\{QR_POSITION_STYLE\}\}/g, qrPositionStyle)
    .replace(/\{\{QR_CONTENT_STYLE\}\}/g, qrContentStyle)
    .replace(/\{\{OPTIMA_FONT\}\}/g, optimaFont)
    .replace(/\{\{SNELL_FONT\}\}/g, snellFont)
    .replace(/\{\{GLACIAL_FONT\}\}/g, glacialFont)
    .replace(/\{\{BACKGROUND_IMAGE_STYLE\}\}/g, backgroundImageStyle)
    .replace(/\{\{OVERLAY_DISPLAY\}\}/g, overlayDisplay)
    .replace(/\{\{EVENT_NAME_DISPLAY\}\}/g, eventNameDisplay)
    .replace(/\{\{GUEST_NAME_DISPLAY\}\}/g, guestNameDisplay)
    .replace(/\{\{ADMITS_BADGE_DISPLAY\}\}/g, admitsBadgeDisplay);
}
